import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { NotesProvider } from '../../context/NotesContext';
import useScreenSize from '../../hooks/useScreenSize';
import CategoryList from './CategoryList';
import NoteList from './NoteList';
import NoteEditor from './NoteEditor';
import MobileNavBar from './MobileNavBar';
import BrandLogo from '../BrandLogo';
import '../../styles/glass-morphism.css';
import '../../styles/responsive.css';

function NotesLayout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [mobileView, setMobileView] = useState('notes');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Mobile Layout Renderer (< 768px)
  const renderMobile = () => (
    <div style={styles.mobileContainer} className="cloud-background">
      {/* Mobile Header */}
      <header style={styles.mobileHeader} className="glass-container cloudnotes-app-header">
        <BrandLogo compact bare />
        <div style={styles.mobileHeaderRight}>
          <button type="button" className="internal-header-action" onClick={() => navigate('/settings')} style={styles.mobileIconButton} title="Settings" aria-label="Settings">
            ⚙️
          </button>
          {user?.role === 'admin' && (
            <button type="button" className="internal-header-action" onClick={() => navigate('/admin')} style={styles.mobileIconButton} title="Admin" aria-label="Admin dashboard">
              👤
            </button>
          )}
          <button type="button" className="internal-header-action" onClick={handleLogout} style={styles.mobileIconButton} title="Logout" aria-label="Log out">
            🚪
          </button>
        </div>
      </header>

      {/* Mobile Content - Conditionally render based on active tab */}
      <main style={styles.mobileContent}>
        {mobileView === 'categories' && (
          <div style={styles.mobileView}>
            <CategoryList />
          </div>
        )}
        {mobileView === 'notes' && (
          <div style={styles.mobileView}>
            <NoteList />
          </div>
        )}
        {mobileView === 'editor' && (
          <div style={styles.mobileView}>
            <NoteEditor />
          </div>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileNavBar activeView={mobileView} setActiveView={setMobileView} />
    </div>
  );

  // Tablet Layout Renderer (768px - 1023px)
  const renderTablet = () => (
    <div style={styles.tabletContainer} className="cloud-background">
      {/* Tablet Header */}
      <header style={styles.tabletHeader} className="glass-container cloudnotes-app-header">
        <div style={styles.headerContent}>
          <BrandLogo compact bare onClick={() => navigate('/notes')} title="Go to notes" />
          <div style={styles.headerRight}>
            <span style={styles.username}>Welcome, {user?.username}</span>
            <button type="button" className="internal-header-action" onClick={() => navigate('/settings')} style={styles.logoutButton}>
              Settings
            </button>
            {user?.role === 'admin' && (
              <button type="button" className="internal-header-action" onClick={() => navigate('/admin')} style={styles.logoutButton}>
                Admin
              </button>
            )}
            <button type="button" className="internal-header-action" onClick={handleLogout} style={styles.logoutButton}>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tablet 2-Column Layout */}
      <main style={styles.tabletMain}>
        {/* Left Panel - Categories + Notes (40%) */}
        <div style={styles.tabletLeftPanel}>
          <div style={styles.tabletCategoriesSection} className="glass-container glass-scrollbar">
            <CategoryList />
          </div>
          <div style={styles.tabletNotesSection} className="glass-container glass-scrollbar">
            <NoteList />
          </div>
        </div>

        {/* Right Panel - Editor (60%) */}
        <div style={styles.tabletRightPanel} className="glass-card">
          <NoteEditor />
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={styles.footerText}>
          Cloudnotes • A product of Inframodule • Free & Open Source
        </p>
      </footer>
    </div>
  );

  // Desktop Layout Renderer (>= 1024px)
  const renderDesktop = () => (
    <div style={styles.container} className="cloud-background">
      <style>{`
        .notes-logo:hover {
          opacity: 0.8;
        }
      `}</style>
      {/* Header */}
      <header style={styles.header} className="glass-container cloudnotes-app-header">
        <div style={styles.headerContent}>
          <BrandLogo bare onClick={() => navigate('/notes')} title="Go to notes" />
          <div style={styles.headerRight}>
            <span style={styles.username}>Welcome, {user?.username}</span>
            <button type="button" className="internal-header-action" onClick={() => navigate('/settings')} style={styles.logoutButton}>
              Settings
            </button>
            {user?.role === 'admin' && (
              <button type="button" className="internal-header-action" onClick={() => navigate('/admin')} style={styles.logoutButton}>
                Admin
              </button>
            )}
            <button type="button" className="internal-header-action" onClick={handleLogout} style={styles.logoutButton}>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main 3-Column Layout */}
      <main style={styles.main}>
        {/* Left Column - Categories (20%) */}
        <div style={styles.leftColumn} className="glass-container glass-scrollbar">
          <CategoryList />
        </div>

        {/* Middle Column - Note List (30%) */}
        <div style={styles.middleColumn} className="glass-container glass-scrollbar">
          <NoteList />
        </div>

        {/* Right Column - Note Editor (50%) */}
        <div style={styles.rightColumn} className="glass-card">
          <NoteEditor />
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={styles.footerText}>
          Cloudnotes • A product of Inframodule • Free & Open Source
        </p>
      </footer>
    </div>
  );

  return (
    <NotesProvider>
      {isMobile && renderMobile()}
      {isTablet && renderTablet()}
      {(isDesktop || (!isMobile && !isTablet)) && renderDesktop()}
    </NotesProvider>
  );
}

const styles = {
  // ============ Desktop Styles (Original) ============
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    overflow: 'hidden',
    boxSizing: 'border-box',
    background: '#f3f9fc',
    position: 'relative',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 0,
    pointerEvents: 'none',
  },
  header: {
    flexShrink: 0,
    marginBottom: '20px',
    padding: '16px 24px',
    position: 'relative',
    zIndex: 1,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    color: 'white',
    fontSize: '1.8rem',
    fontWeight: 'bold',
    margin: 0,
    cursor: 'pointer',
    transition: 'opacity 0.3s ease',
    userSelect: 'none',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  username: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '1rem',
  },
  logoutButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: 'white',
    padding: '8px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  main: {
    flex: 1,
    display: 'flex',
    gap: '20px',
    minHeight: 0,
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
  },
  leftColumn: {
    width: '20%',
    minWidth: '200px',
    height: '100%',
    overflowY: 'auto',
  },
  middleColumn: {
    width: '30%',
    minWidth: '300px',
    height: '100%',
    overflowY: 'auto',
  },
  rightColumn: {
    width: '50%',
    minWidth: '400px',
    height: '100%',
    overflowY: 'auto',
  },
  footer: {
    flexShrink: 0,
    marginTop: '12px',
    padding: '12px 0',
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
  },
  footerText: {
    color: '#52677c',
    fontSize: '12px',
    margin: 0,
    fontWeight: '400',
  },

  // ============ Mobile Styles (< 768px) ============
  mobileContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#f3f9fc',
    position: 'relative',
  },
  mobileHeader: {
    flexShrink: 0,
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
  },
  mobileLogo: {
    color: 'white',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    margin: 0,
    userSelect: 'none',
  },
  mobileHeaderRight: {
    display: 'flex',
    gap: '8px',
  },
  mobileIconButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: 'white',
    minWidth: '44px',
    minHeight: '44px',
    borderRadius: '8px',
    fontSize: '18px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileContent: {
    flex: 1,
    overflow: 'auto',
    padding: '12px',
    paddingBottom: '68px', // Extra space for bottom nav
    position: 'relative',
    zIndex: 1,
  },
  mobileView: {
    height: '100%',
    overflow: 'auto',
  },

  // ============ Tablet Styles (768px - 1023px) ============
  tabletContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    overflow: 'hidden',
    boxSizing: 'border-box',
    background: '#f3f9fc',
    position: 'relative',
  },
  tabletHeader: {
    flexShrink: 0,
    marginBottom: '16px',
    padding: '14px 20px',
    position: 'relative',
    zIndex: 1,
  },
  tabletLogo: {
    color: 'white',
    fontSize: '1.6rem',
    fontWeight: 'bold',
    margin: 0,
    cursor: 'pointer',
    transition: 'opacity 0.3s ease',
    userSelect: 'none',
  },
  tabletMain: {
    flex: 1,
    display: 'flex',
    gap: '16px',
    minHeight: 0,
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
  },
  tabletLeftPanel: {
    width: '40%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'hidden',
  },
  tabletCategoriesSection: {
    height: '30%',
    minHeight: '150px',
    overflowY: 'auto',
  },
  tabletNotesSection: {
    flex: 1,
    overflowY: 'auto',
  },
  tabletRightPanel: {
    width: '60%',
    overflowY: 'auto',
  },
};

export default NotesLayout;
